import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CellSub,
  CodeChip,
  EmptyState,
  KeyValue,
  Skeleton,
  Table,
  TableShell,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  KeyRound,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Send,
  Webhook,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CreateKeyDialog,
  RevokeKeyDialog,
  RotateKeyDialog,
} from '../features/integrators/ApiKeyDialogs';
import { ApiError, api } from '../lib/api';
import { auDate, auDateTime, relative } from '../lib/format';
import { ApiKeyStateBadge, DeliveryStatusBadge, IntegratorStatusBadge } from '../lib/status';

interface IntegratorDetailData {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: string;
  contactName: string | null;
  contactEmail: string;
  contactPhone: string | null;
  websiteUrl: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  defaultRateLimitPerMinute: number;
  createdAt: string;
  keys: Array<{
    id: string;
    keyId: string;
    label: string;
    environment: string;
    last4: string;
    scopes: string[];
    ipAllowlist: string[];
    expiresAt: string | null;
    lastUsedAt: string | null;
    lastUsedIp: string | null;
    revokedAt: string | null;
    revokedReason: string | null;
    rotatedFromKeyId: string | null;
    createdAt: string;
    state: string;
  }>;
  grants: Array<{
    id: string;
    chamberSlug: string;
    chamberName: string;
    organisationScope: string;
    organisationIds: string[];
    operations: string[];
    active: boolean;
    revokedAt: string | null;
  }>;
  webhooks: Array<{
    id: string;
    url: string;
    description: string | null;
    chamberSlug: string | null;
    events: string[];
    active: boolean;
    deliveryCount: number;
    alertingSince: string | null;
    secretRotatedAt: string | null;
  }>;
}

interface Delivery {
  id: string;
  eventType: string;
  eventId: string;
  attempt: number;
  status: string;
  responseStatus: number | null;
  responseBodyExcerpt: string | null;
  errorMessage: string | null;
  latencyMs: number | null;
  nextAttemptAt: string | null;
  deadLetteredAt: string | null;
  createdAt: string;
}

/**
 * One integrator: keys, chamber grants, webhooks and usage (spec v3 §20.3).
 *
 * The screen is built for one job — diagnosing an integration that is misbehaving —
 * because that is why anyone opens it. Hence the delivery log with response codes and
 * replay, the raw recent-errors list, and the latency percentiles. Without these,
 * answering "SageDocs says the API is broken" means reading production logs, which does
 * not scale past the first integrator.
 */
export function IntegratorDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [rotating, setRotating] = useState<{ id: string; label: string } | null>(null);
  const [revoking, setRevoking] = useState<{ id: string; label: string } | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['integrator', id],
    queryFn: () => api.get<IntegratorDetailData>(`/api/console/platform/integrators/${id}`),
    enabled: Boolean(id),
  });

  const usage = useQuery({
    queryKey: ['integrator-usage', id],
    queryFn: () =>
      api.get<{
        byEndpoint: Array<{ path: string; status: number; count: number; avgLatencyMs: number }>;
        byStatus: Array<{ status: number; count: number }>;
        byDay: Array<{ date: string; count: number; errors: number }>;
        latency: { p50: number; p95: number; p99: number };
        recentErrors: Array<{
          createdAt: string;
          method: string;
          path: string;
          status: number;
          errorCode: string | null;
          ip: string | null;
          requestId: string;
        }>;
      }>(`/api/console/platform/integrators/${id}/usage`, { days: 7 }),
    enabled: Boolean(id),
  });

  const deliveries = useQuery({
    queryKey: ['webhook-deliveries', selectedEndpoint],
    queryFn: () =>
      api.get<Delivery[]>(`/api/console/platform/integrators/webhooks/${selectedEndpoint}/deliveries`, {
        limit: 50,
      }),
    enabled: Boolean(selectedEndpoint),
  });

  const suspend = useMutation({
    mutationFn: (reason: string) => api.post(`/api/console/platform/integrators/${id}/suspend`, { reason }),
    onSuccess: () => {
      toast.success('Integrator suspended', 'Every one of its keys now fails closed.');
      void queryClient.invalidateQueries({ queryKey: ['integrator', id] });
    },
    onError: (error) => toast.error('Could not suspend', error instanceof ApiError ? error.message : ''),
  });

  const reinstate = useMutation({
    mutationFn: () => api.post(`/api/console/platform/integrators/${id}/reinstate`),
    onSuccess: () => {
      toast.success('Integrator reinstated');
      void queryClient.invalidateQueries({ queryKey: ['integrator', id] });
    },
  });

  const revokeGrant = useMutation({
    mutationFn: (grantId: string) => api.del(`/api/console/platform/integrators/grants/${grantId}`),
    onSuccess: () => {
      toast.success('Access revoked', 'The integrator can no longer act for that chamber.');
      void queryClient.invalidateQueries({ queryKey: ['integrator', id] });
    },
  });

  const replay = useMutation({
    mutationFn: (deliveryId: string) =>
      api.post(`/api/console/platform/integrators/webhooks/deliveries/${deliveryId}/replay`),
    onSuccess: () => {
      toast.success('Delivery queued for replay');
      void queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', selectedEndpoint] });
    },
  });

  const sendTest = useMutation({
    mutationFn: (endpointId: string) =>
      api.post(`/api/console/platform/integrators/webhooks/${endpointId}/test`, { chamberId: null }),
    onSuccess: () => {
      toast.success('Test event sent', 'Check the delivery log for the result.');
      void queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', selectedEndpoint] });
    },
  });

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!query.data) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Integrator not found">
        <Link to="/platform/integrators" className="underline">
          Back to integrators
        </Link>
      </Callout>
    );
  }

  const integrator = query.data;
  const activeKeys = integrator.keys.filter((k) => k.state === 'ACTIVE' || k.state === 'EXPIRING');
  const alertingWebhooks = integrator.webhooks.filter((w) => w.alertingSince);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/platform/integrators"
          className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
        >
          <ArrowLeft aria-hidden className="size-3.5" /> All integrators
        </Link>
        <div className="mt-2 flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="[font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
                {integrator.name}
              </h1>
              <IntegratorStatusBadge status={integrator.status} />
              <CodeChip>{integrator.slug}</CodeChip>
            </div>
            {integrator.description && (
              <p className="pv-lede mt-2">{integrator.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {integrator.status === 'ACTIVE' ? (
              <Button
                variant="secondary"
                leadingIcon={<Pause className="size-3.5" />}
                loading={suspend.isPending}
                onClick={() => {
                  const reason = window.prompt(
                    'Why is this integrator being suspended? Every one of its keys will fail closed immediately.',
                  );
                  if (reason?.trim()) suspend.mutate(reason.trim());
                }}
              >
                Suspend
              </Button>
            ) : integrator.status === 'SUSPENDED' ? (
              <Button
                variant="primary"
                leadingIcon={<Play className="size-3.5" />}
                loading={reinstate.isPending}
                onClick={() => reinstate.mutate()}
              >
                Reinstate
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {integrator.status === 'SUSPENDED' && (
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Suspended">
          {integrator.suspendedReason} · {auDateTime(integrator.suspendedAt)}. Every request from
          this integrator fails with <CodeChip>integrator_suspended</CodeChip>.
        </Callout>
      )}

      {activeKeys.length === 0 && integrator.status === 'ACTIVE' && (
        <Callout tone="warn" icon={<KeyRound className="size-4" />} title="No usable key">
          This integrator has no active key, so it cannot call the API at all. Issue one below.
        </Callout>
      )}

      {alertingWebhooks.length > 0 && (
        <Callout
          tone="stop"
          icon={<Webhook className="size-4" />}
          title="A webhook endpoint is failing"
          actions={
            <Button variant="secondary" size="sm" onClick={() => setSelectedEndpoint(alertingWebhooks[0]!.id)}>
              Open the delivery log
            </Button>
          }
        >
          {alertingWebhooks.map((w) => (
            <p key={w.id} className="break-all">
              {w.url} has been dead-lettering since {relative(w.alertingSince!)}. The integrator is
              missing events — the polling endpoint is their reconciliation path in the meantime.
            </p>
          ))}
        </Callout>
      )}

      <Tabs defaultValue="keys">
        <TabsList>
          <TabsTrigger value="keys" count={integrator.keys.length}>
            Keys
          </TabsTrigger>
          <TabsTrigger value="access" count={integrator.grants.filter((g) => g.active).length}>
            Chamber access
          </TabsTrigger>
          <TabsTrigger value="webhooks" count={integrator.webhooks.length}>
            Webhooks
          </TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
        </TabsList>

        {/* ── Keys ──────────────────────────────────────────────────────── */}
        <TabsContent value="keys" className="pt-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="[font-size:13px] text-[var(--pv-muted)]">
              A secret is shown once at creation and only its hash is stored. Rotate with an
              overlap so nothing breaks; revoke only when a key must stop now.
            </p>
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="size-3.5" />}
              onClick={() => setCreateKeyOpen(true)}
            >
              Issue a key
            </Button>
          </div>

          {integrator.keys.length === 0 ? (
            <EmptyState
              icon={<KeyRound className="size-5" />}
              title="No keys issued"
              description="Without a key this integrator cannot call the API."
              action={
                <Button variant="primary" onClick={() => setCreateKeyOpen(true)}>
                  Issue the first key
                </Button>
              }
            />
          ) : (
            <TableShell>
              <Table className="min-w-[900px]">
                <Thead>
                  <Tr>
                    <Th>Label</Th>
                    <Th>Key id</Th>
                    <Th>Environment</Th>
                    <Th>Scope</Th>
                    <Th>Last used</Th>
                    <Th>State</Th>
                    <Th className="w-40" />
                  </Tr>
                </Thead>
                <Tbody>
                  {integrator.keys.map((key) => (
                    <Tr key={key.id}>
                      <Td>
                        <span className="font-medium">{key.label}</span>
                        <CellSub>
                          created {auDate(key.createdAt)}
                          {key.rotatedFromKeyId && ` · rotated from ${key.rotatedFromKeyId}`}
                        </CellSub>
                      </Td>
                      <Td>
                        <CodeChip>{key.keyId}</CodeChip>
                        <CellSub>…{key.last4}</CellSub>
                      </Td>
                      <Td>
                        <Badge tone={key.environment === 'LIVE' ? 'ink' : 'neutral'} size="sm">
                          {key.environment}
                        </Badge>
                      </Td>
                      <Td>
                        {key.scopes.length === 0 ? (
                          <span className="[font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
                            Inherits grants
                          </span>
                        ) : (
                          <Tooltip content={key.scopes.join(', ')}>
                            <span className="[font-size:var(--pv-text-xs)]">
                              {key.scopes.length} scope{key.scopes.length === 1 ? '' : 's'}
                            </span>
                          </Tooltip>
                        )}
                        {key.ipAllowlist.length > 0 && (
                          <CellSub>{key.ipAllowlist.length} IP restriction(s)</CellSub>
                        )}
                      </Td>
                      <Td>
                        {key.lastUsedAt ? (
                          <>
                            {relative(key.lastUsedAt)}
                            {key.lastUsedIp && <CellSub className="pv-mono">{key.lastUsedIp}</CellSub>}
                          </>
                        ) : (
                          <span className="text-[var(--pv-faint)]">Never</span>
                        )}
                      </Td>
                      <Td>
                        <ApiKeyStateBadge state={key.state} />
                        {key.expiresAt && key.state !== 'REVOKED' && (
                          <CellSub>{auDate(key.expiresAt)}</CellSub>
                        )}
                        {key.revokedReason && <CellSub>{key.revokedReason}</CellSub>}
                      </Td>
                      <Td>
                        {key.state !== 'REVOKED' && (
                          <span className="flex gap-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRotating({ id: key.id, label: key.label })}
                            >
                              Rotate
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[var(--pv-stop)]"
                              onClick={() => setRevoking({ id: key.id, label: key.label })}
                            >
                              Revoke
                            </Button>
                          </span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableShell>
          )}
        </TabsContent>

        {/* ── Chamber access ────────────────────────────────────────────── */}
        <TabsContent value="access" className="pt-4">
          <p className="mb-3 [font-size:13px] text-[var(--pv-muted)]">
            Grants are separate from keys on purpose: a chamber can be added or revoked without
            reissuing credentials, and revoking a leaked key does not cost the integrator its
            chamber relationships.
          </p>
          {integrator.grants.length === 0 ? (
            <EmptyState
              title="No chamber access"
              description="This integrator is not authorised for any chamber, so every request it makes will be refused with chamber_not_granted."
            />
          ) : (
            <TableShell>
              <Table className="min-w-[760px]">
                <Thead>
                  <Tr>
                    <Th>Chamber</Th>
                    <Th>Exporters</Th>
                    <Th>Operations</Th>
                    <Th>Status</Th>
                    <Th className="w-28" />
                  </Tr>
                </Thead>
                <Tbody>
                  {integrator.grants.map((grant) => (
                    <Tr key={grant.id}>
                      <Td>
                        <span className="font-medium">{grant.chamberName}</span>
                        <CellSub className="pv-mono">{grant.chamberSlug}</CellSub>
                      </Td>
                      <Td>
                        {grant.organisationScope === 'ALL' ? (
                          'All exporters'
                        ) : (
                          <>
                            {grant.organisationIds.length} specific
                            <CellSub>Narrowed to named exporters</CellSub>
                          </>
                        )}
                      </Td>
                      <Td>
                        <span className="flex flex-wrap gap-1">
                          {grant.operations.map((op) => (
                            <CodeChip key={op}>{op}</CodeChip>
                          ))}
                        </span>
                      </Td>
                      <Td>
                        <Badge tone={grant.active ? 'ok' : 'neutral'} size="sm" dot={grant.active}>
                          {grant.active ? 'Granted' : `Revoked ${auDate(grant.revokedAt)}`}
                        </Badge>
                      </Td>
                      <Td>
                        {grant.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[var(--pv-stop)]"
                            loading={revokeGrant.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Revoke ${integrator.name}'s access to ${grant.chamberName}? Their existing keys keep working for other chambers.`,
                                )
                              ) {
                                revokeGrant.mutate(grant.id);
                              }
                            }}
                          >
                            Revoke
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableShell>
          )}
        </TabsContent>

        {/* ── Webhooks ─────────────────────────────────────────────────── */}
        <TabsContent value="webhooks" className="pt-4">
          <p className="mb-3 [font-size:13px] text-[var(--pv-muted)]">
            Webhook delivery fails silently more often than anything else in an integration, so
            every attempt is recorded here with its response and can be replayed.
          </p>

          <div className="flex flex-col gap-3">
            {integrator.webhooks.map((endpoint) => (
              <Card key={endpoint.id}>
                <div className="flex flex-wrap items-start gap-3">
                  <Webhook aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--pv-faint)]" />
                  <div className="min-w-0 flex-1">
                    <p className="break-all [font-size:13.5px] font-medium">{endpoint.url}</p>
                    <p className="mt-0.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                      {endpoint.chamberSlug ? `Chamber ${endpoint.chamberSlug}` : 'All granted chambers'} ·{' '}
                      {endpoint.deliveryCount} deliveries
                      {endpoint.secretRotatedAt && ` · secret rotated ${relative(endpoint.secretRotatedAt)}`}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {endpoint.events.map((event) => (
                        <CodeChip key={event}>{event}</CodeChip>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {endpoint.alertingSince && (
                      <Badge tone="stop" dot size="sm">
                        Failing
                      </Badge>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<Send className="size-3.5" />}
                      loading={sendTest.isPending}
                      onClick={() => {
                        setSelectedEndpoint(endpoint.id);
                        sendTest.mutate(endpoint.id);
                      }}
                    >
                      Test
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setSelectedEndpoint((current) => (current === endpoint.id ? null : endpoint.id))
                      }
                    >
                      {selectedEndpoint === endpoint.id ? 'Hide' : 'Deliveries'}
                    </Button>
                  </div>
                </div>

                {selectedEndpoint === endpoint.id && (
                  <div className="mt-4 border-t border-[var(--pv-line)] pt-4">
                    {deliveries.isLoading ? (
                      <Skeleton className="h-32" />
                    ) : (deliveries.data ?? []).length === 0 ? (
                      <p className="[font-size:13px] text-[var(--pv-muted)]">
                        No deliveries recorded for this endpoint yet.
                      </p>
                    ) : (
                      <TableShell>
                        <Table className="min-w-[820px]">
                          <Thead>
                            <Tr>
                              <Th>Event</Th>
                              <Th>When</Th>
                              <Th numeric>Attempt</Th>
                              <Th numeric>Response</Th>
                              <Th numeric>Latency</Th>
                              <Th>Status</Th>
                              <Th className="w-24" />
                            </Tr>
                          </Thead>
                          <Tbody>
                            {deliveries.data!.map((delivery) => (
                              <Tr key={delivery.id}>
                                <Td>
                                  <CodeChip>{delivery.eventType}</CodeChip>
                                </Td>
                                <Td>{auDateTime(delivery.createdAt)}</Td>
                                <Td numeric>{delivery.attempt}</Td>
                                <Td numeric>
                                  {delivery.responseStatus ?? (
                                    <Tooltip content={delivery.errorMessage ?? 'No response'}>
                                      <span className="text-[var(--pv-stop)]">error</span>
                                    </Tooltip>
                                  )}
                                </Td>
                                <Td numeric>{delivery.latencyMs ? `${delivery.latencyMs}ms` : '—'}</Td>
                                <Td>
                                  <DeliveryStatusBadge status={delivery.status} />
                                  {delivery.nextAttemptAt && (
                                    <CellSub>retry {relative(delivery.nextAttemptAt)}</CellSub>
                                  )}
                                </Td>
                                <Td>
                                  {delivery.status !== 'SUCCEEDED' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      leadingIcon={<RefreshCw className="size-3" />}
                                      onClick={() => replay.mutate(delivery.id)}
                                    >
                                      Replay
                                    </Button>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableShell>
                    )}
                  </div>
                )}
              </Card>
            ))}

            {integrator.webhooks.length === 0 && (
              <EmptyState
                icon={<Webhook className="size-5" />}
                title="No webhook endpoints"
                description="Without one, the integrator only learns about decisions by polling the outstanding endpoint — which is a supported and arguably more reliable pattern."
              />
            )}
          </div>
        </TabsContent>

        {/* ── Usage ────────────────────────────────────────────────────── */}
        <TabsContent value="usage" className="pt-4">
          {usage.isLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-3">
                {(['p50', 'p95', 'p99'] as const).map((percentile) => (
                  <Card key={percentile}>
                    <p className="pv-eyebrow text-[var(--pv-faint)]">{percentile} latency</p>
                    <p className="pv-num mt-1.5 [font-size:24px] font-bold leading-none">
                      {Math.round(usage.data?.latency[percentile] ?? 0)}
                      <span className="ml-1 [font-size:13px] font-normal text-[var(--pv-muted)]">ms</span>
                    </p>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader title="Requests by endpoint" hint="Last 7 days" />
                {(usage.data?.byEndpoint ?? []).length === 0 ? (
                  <p className="[font-size:13px] text-[var(--pv-muted)]">
                    No API traffic from this integrator in the last 7 days.
                  </p>
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Endpoint</Th>
                        <Th numeric>Status</Th>
                        <Th numeric>Requests</Th>
                        <Th numeric>Avg latency</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {usage.data!.byEndpoint.map((row) => (
                        <Tr key={`${row.path}-${row.status}`}>
                          <Td>
                            <span className="pv-mono [font-size:12.5px]">{row.path}</span>
                          </Td>
                          <Td numeric>
                            <Badge tone={row.status >= 400 ? 'stop' : 'ok'} size="sm">
                              {row.status}
                            </Badge>
                          </Td>
                          <Td numeric>{row.count.toLocaleString()}</Td>
                          <Td numeric>{row.avgLatencyMs}ms</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Card>

              <Card>
                <CardHeader
                  title="Recent errors"
                  hint="Last 50"
                  description="What the integrator's engineer will ask you about. Quote the request id."
                />
                {(usage.data?.recentErrors ?? []).length === 0 ? (
                  <p className="[font-size:13px] text-[var(--pv-ok)]">No errors in the last 7 days.</p>
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>When</Th>
                        <Th>Request</Th>
                        <Th numeric>Status</Th>
                        <Th>Error code</Th>
                        <Th>Request id</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {usage.data!.recentErrors.map((error) => (
                        <Tr key={error.requestId + error.createdAt}>
                          <Td>{auDateTime(error.createdAt)}</Td>
                          <Td>
                            <span className="pv-mono [font-size:12.5px]">
                              {error.method} {error.path}
                            </span>
                            {error.ip && <CellSub className="pv-mono">{error.ip}</CellSub>}
                          </Td>
                          <Td numeric>
                            <Badge tone="stop" size="sm">
                              {error.status}
                            </Badge>
                          </Td>
                          <Td>{error.errorCode ? <CodeChip>{error.errorCode}</CodeChip> : '—'}</Td>
                          <Td>
                            <span className="pv-mono [font-size:11px] text-[var(--pv-faint)]">
                              {error.requestId.slice(0, 8)}
                            </span>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="overview" className="pt-4">
          <Card>
            <CardHeader title="Integrator details" />
            <dl className="m-0 grid gap-x-8 md:grid-cols-2">
              <KeyValue label="Legal name">{integrator.name}</KeyValue>
              <KeyValue label="Slug" mono>
                {integrator.slug}
              </KeyValue>
              <KeyValue label="Technical contact">{integrator.contactEmail}</KeyValue>
              <KeyValue label="Contact name">{integrator.contactName ?? '—'}</KeyValue>
              <KeyValue label="Phone">{integrator.contactPhone ?? '—'}</KeyValue>
              <KeyValue label="Website">
                {integrator.websiteUrl ? (
                  <a href={integrator.websiteUrl} target="_blank" rel="noreferrer" className="underline">
                    {integrator.websiteUrl}
                  </a>
                ) : (
                  '—'
                )}
              </KeyValue>
              <KeyValue label="Rate limit">
                {integrator.defaultRateLimitPerMinute.toLocaleString()} requests/minute
              </KeyValue>
              <KeyValue label="Onboarded">{auDate(integrator.createdAt)}</KeyValue>
            </dl>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateKeyDialog
        integratorId={integrator.id}
        integratorName={integrator.name}
        open={createKeyOpen}
        onOpenChange={setCreateKeyOpen}
      />
      {rotating && (
        <RotateKeyDialog
          keyRowId={rotating.id}
          keyLabel={rotating.label}
          integratorId={integrator.id}
          open
          onOpenChange={(next) => !next && setRotating(null)}
        />
      )}
      {revoking && (
        <RevokeKeyDialog
          keyRowId={revoking.id}
          keyLabel={revoking.label}
          integratorId={integrator.id}
          open
          onOpenChange={(next) => !next && setRevoking(null)}
        />
      )}
    </div>
  );
}
